import React, {useEffect, useState} from 'react';
import { useForm } from 'react-hook-form';
import { parseISO } from 'date-fns';
import { statesAbbr } from "../../global/utils";
import { useStyles as classes } from "../../styles/styles";
import { validateRockVyRequest, getRockVyRequest, isOnline } from "./optionFunctions";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers";
import FormControl from '@mui/material/FormControl';
import FormLabel from "@mui/material/FormLabel";
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import { PatternFormat } from "react-number-format";
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from "@mui/material/FormControlLabel";
import DOB from '../Dob';
import Checkbox from "@mui/material/Checkbox";
import Stack from '@mui/material/Stack';

const RockVy = (props) => {
    const requestData = props.requestData;
    const setData = props.setData;
    const setErrorMessage = props.setErrorMessage;
    const court = props.court;
    const balance = props.balance;
    const { register, getValues, setValue } = useForm({shouldUnregister: false});
    const [county, setCounty] = useState(null);
    const [dob, setDob] = useState(null);
    const [phone, setPhone] = useState(null);
    const [violationDate, setViolationDate] = useState(balance.violationDate ? parseISO(balance.violationDate) : null);
    const [classSelection, setClassSelection] = useState(null);
    const [radios, setRadios] = useState({sex: null, saturdayMorning: '0', saturdayAfternoon: '0', weekdayEvening: '0', priorSupervision: null});
    const [ethnicity, setEthnicity] = useState('');
    const [education, setEducation] = useState('');

    const handlePhoneChange = (values) => {
        const val = values.value;
        setPhone(val && val.length === 10 ? val : null);
    }

    useEffect(() => {
        if (court && !county) {
            const idx = court.courtName.indexOf(' ');
            setCounty(court.courtName.substring(0, idx));
        }
    }, [court, county])

    useEffect(() => {
        if (requestData) {
            const form = getValues();
            const errors = validateRockVyRequest(form, phone, radios, dob, violationDate);
            if (errors) {
                setErrorMessage(`Please correct the following errors before proceeding: ${errors}`);
            } else setData(getRockVyRequest(form, balance, county, radios, dob, violationDate));
        }
    }, [requestData])

    useEffect(() => {
        if (balance && isOnline(balance.description)) {
            setValue('classType', 'online');
            setClassSelection('online');
        } else {
            setValue('classType', 'inPerson');
            setClassSelection('inPerson');
        }
    }, [balance]);

    return (
        <Grid container spacing={2} alignItems='top' >
            <Typography variant='body2' component='p' sx={{margin: '0.5rem 1rem 1rem 1rem'}}>
                Please provide the required information below in order to process your traffic school program application at {court.courtName}.
            </Typography>
            <Typography variant='subtitle2' color='secondary' sx={{margin: '.3rem 1rem 0 1rem', width: '100%'}}>
                If you have a CDL, do not submit this application form, call the Rock Valley College at (815) 921-3940.
            </Typography>
            <Grid item xs={12} sm={6} lg={4}>
                <FormControl name='lastName' fullWidth sx={classes.formControl} {...register('lastName')}>
                    <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                        Last Name
                    </FormLabel>
                    <TextField name='lastName' variant='outlined' fullWidth autoComplete='off' inputProps={{maxLength: 100, sx: classes.textbox, "aria-required": "true"}} />
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
                <FormControl name='firstName' fullWidth sx={classes.formControl} {...register('firstName')}>
                    <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                        First Name
                    </FormLabel>
                    <TextField name='firstName' variant='outlined' fullWidth autoComplete='off' inputProps={{maxLength: 100, sx: classes.textbox, "aria-required": "true"}} />
                </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} lg={1}>
                <FormControl name='middleInitial' fullWidth sx={classes.formControl} {...register('middleInitial')}>
                    <FormLabel shrink={true} sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                        Mid. I.
                    </FormLabel>
                    <TextField name='middleInitial' variant='outlined' fullWidth autoComplete='off' inputProps={{maxLength: 5, sx: classes.textbox}} />
                </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} lg={3}>
                <FormControl name='sex' fullWidth sx={classes.formControl}>
                    <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                        Sex
                    </FormLabel>
                    <RadioGroup
                        row={true}
                        name='sex'
                        sx={{padding: '0'}}
                        aria-required="true"
                        onChange={(event, value) => setRadios({...radios, sex: value})}
                    >
                        <FormControlLabel value='M' control={<Radio size='small' sx={{paddingTop: '.35rem', paddingBottom: {xs: '.5rem', sm: '0', md: '.5rem'}}} />} label='Male' />
                        <FormControlLabel value='F' control={<Radio size='small' sx={{paddingTop: '.35rem'}} />} label='Female' />
                    </RadioGroup>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <FormControl name='address' fullWidth sx={classes.formControl} {...register('address')}>
                    <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                        Address
                    </FormLabel>
                    <TextField name='address' variant='outlined' fullWidth autoComplete='off' inputProps={{maxLength: 100, sx: classes.textbox, "aria-required": "true"}} />
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <FormControl name='city' fullWidth sx={classes.formControl} {...register('city')}>
                    <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                        City
                    </FormLabel>
                    <TextField name='city' variant='outlined' fullWidth autoComplete='address-level2' inputProps={{maxLength: 100, sx: classes.textbox, "aria-required": "true"}} />
                </FormControl>
            </Grid>
            <Grid item xs={5} sm={2} md={2}>
                <FormControl name='state' fullWidth sx={classes.formControl} {...register('state')}>
                    <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                        State
                    </FormLabel>
                    <Select
                        name='state'
                        native
                        variant='outlined'
                        fullWidth
                        sx={{height: '2rem'}}
                        inputProps={{
                            "aria-required": "true"
                        }}
                    >
                        <option key='none' value=''>Enter your state</option>
                        {statesAbbr.map((state) => (
                            <option key={state.key} value={state.value} style={{fontSize: '.9rem'}}>{state.text}</option>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={7} sm={4} md={2}>
                <FormControl name='zip' fullWidth sx={classes.formControl} {...register('zip')}>
                    <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                        Zip
                    </FormLabel>
                    <PatternFormat
                        id='zip'
                        name='zip'
                        customInput={TextField}
                        type='text'
                        autoComplete='postal-code'
                        variant='outlined'
                        format='#####'
                        InputProps={{
                            style: classes.numberbox,
                            "aria-required": "true"
                        }}
                    />
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
                <DOB dob={dob} setDob={setDob} required={true} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <FormControl name='phone' fullWidth sx={classes.formControl} {...register('phone')}>
                    <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                        Mobile Phone
                    </FormLabel>
                    <PatternFormat
                        name='phone'
                        customInput={TextField}
                        type='text'
                        autoComplete='tel-national'
                        variant='outlined'
                        format="(###) ###-####"
                        onValueChange={handlePhoneChange}
                        InputProps={{
                            style: classes.numberbox,
                            "aria-required": "true"
                        }}
                    />
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <FormControl name='email' fullWidth sx={classes.formControl} {...register('email')}>
                    <FormLabel required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                        Email Address
                    </FormLabel>
                    <TextField
                        name='email'
                        variant='outlined'
                        fullWidth
                        autoComplete='off'
                        inputProps={{maxLength: 100, sx: classes.textbox, "aria-required": "true"}}
                    />
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth sx={classes.formControl}>
                    <FormLabel shrink={true} sx={classes.inputLabel}>
                        Date of Ticket
                    </FormLabel>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            name='violationDate'
                            disableOpenPicker={false}
                            disableMaskedInput={false}
                            format='MM/dd/yyyy'
                            defaultValue={null}
                            value={violationDate}
                            onChange={(newValue) => {
                                setViolationDate(newValue)
                            }}
                            slotProps={{
                                textField: {
                                    id: 'dob',
                                    inputProps: {
                                        sx: classes.textbox,
                                        "aria-required": "true"
                                    }
                                }
                            }}
                        />
                    </LocalizationProvider>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
                <FormControl name='license' fullWidth sx={classes.formControl} {...register('license')}>
                    <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                        Driver License
                    </FormLabel>
                    <TextField name='license' variant='outlined' fullWidth autoComplete='off' inputProps={{maxLength: 50, sx: classes.textbox,"aria-required": "true"}} />
                </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
                <FormControl name='licenseState' fullWidth sx={classes.formControl} {...register('licenseState')}>
                    <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                        License State
                    </FormLabel>
                    <Select
                        name='licenseState'
                        native
                        variant='outlined'
                        fullWidth
                        sx={{height: '2rem'}}
                        inputProps={{
                            "aria-required": "true"
                        }}
                    >
                        <option key='none' value=''>Enter your state</option>
                        {statesAbbr.map((state) => (
                            <option key={state.key} value={state.value} style={{fontSize: '.9rem'}}>{state.text}</option>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
                <FormControl name='is21' fullWidth sx={classes.formControl} {...register('is21')}>
                    <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                        Age Qualification
                    </FormLabel>
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="is21"
                                color='primary'
                                defaultChecked={false}
                                sx={{marginLeft: '0', paddingLeft: '0'}}
                                inputProps={{"aria-required": "true"}}
                            />
                        }
                        label={<Typography component="span" sx={{fontSize: '1rem'}}>I am 21 or older</Typography>}
                        sx={{...classes.checkbox, marginTop: '.5rem', marginLeft: '0'}}
                    />
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <FormControl name='classType' fullWidth sx={classes.formControl} {...register('classType')}>
                    <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                        ClassType
                    </FormLabel>
                    <Select
                        name='classType'
                        native
                        variant='outlined'
                        onChange={(e) => setClassSelection(e.target.value)}
                        sx={{height: '2rem', width: {xs: '100%', md: '50%'}}}
                        inputProps={{
                            "aria-required": "true"
                        }}
                    >
                        <option key='none' value={null}>Select the class type</option>
                        <option key='inPerson' value='inPerson'>In-person class</option>
                        <option key='online' value='onine'>Online class</option>
                    </Select>
                </FormControl>
            </Grid>
            { classSelection && classSelection === 'inPerson' &&
                <>
                    <Grid xs={12}>
                        <Stack direction='row' spacing={0} sx={{margin: '1rem 1rem 0 1rem'}}>
                            <Typography variant='body1' sx={{color: 'royalblue', fontWeight: 'bold', width: '25vw', marginRight: '1.5rem'}}>
                                Class time order of preference (1, 2 or 3)
                            </Typography>
                            <FormControl name='saturdayMoringPreference' fullWidth sx={classes.formControl}>
                                <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                                    Saturday Morning
                                </FormLabel>
                                <RadioGroup
                                    row={true}
                                    name='saturdayMorningPreference'
                                    sx={{padding: '0'}}
                                    onChange={(event, value) => setRadios({...radios, saturdayMorning: value})}
                                    aria-required="true"
                                >
                                    <FormControlLabel value='1' control={<Radio size='small' sx={{paddingTop: '.35rem'}} />} label='1' />
                                    <FormControlLabel value='2' control={<Radio size='small' sx={{paddingTop: '.35rem'}} />} label='2' />
                                    <FormControlLabel value='3' control={<Radio size='small' sx={{paddingTop: '.35rem'}} />} label='3' />
                                </RadioGroup>
                            </FormControl>
                            <FormControl name='saturdayAfternoonPreference' fullWidth sx={classes.formControl}>
                                <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                                    Saturday Afternoon
                                </FormLabel>
                                <RadioGroup
                                    row={true}
                                    name='saturdayAfternoonPreference'
                                    sx={{padding: '0'}}
                                    onChange={(event, value) => setRadios({...radios, saturdayAfternoon: value})}
                                    aria-required="true"
                                >
                                    <FormControlLabel value='1' control={<Radio size='small' sx={{paddingTop: '.35rem'}} />} label='1' />
                                    <FormControlLabel value='2' control={<Radio size='small' sx={{paddingTop: '.35rem'}} />} label='2' />
                                    <FormControlLabel value='3' control={<Radio size='small' sx={{paddingTop: '.35rem'}} />} label='3' />
                                </RadioGroup>
                            </FormControl>
                            <FormControl name='weekdayEveningPreference' fullWidth sx={classes.formControl}>
                                <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                                    Weekday Evening (6:00 - 10:00 pm)
                                </FormLabel>
                                <RadioGroup
                                    row={true}
                                    name='weekdayEveningPreference'
                                    sx={{padding: '0'}}
                                    onChange={(event, value) => setRadios({...radios, weekdayEvening: value})}
                                    aria-required="true"
                                >
                                    <FormControlLabel value='1' control={<Radio size='small' sx={{paddingTop: '.35rem'}} />} label='1' />
                                    <FormControlLabel value='2' control={<Radio size='small' sx={{paddingTop: '.35rem'}} />} label='2' />
                                    <FormControlLabel value='3' control={<Radio size='small' sx={{paddingTop: '.35rem'}} />} label='3' />
                                </RadioGroup>
                            </FormControl>
                        </Stack>
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                        <FormControl name='classLocation' sx={{...classes.formControl, margin: '1rem', width: '90%'}} {...register('classLocation')}>
                            <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                                Class Location
                            </FormLabel>
                            <Select
                                name='classLocation'
                                native
                                variant='outlined'
                                fullWidth
                                sx={{height: '2rem', width: '25vh'}}
                                inputProps={{
                                    "aria-required": "true"
                                }}
                            >
                                <option key='none' value={null}>Select the class location</option>
                                <option key='RockfordIL' value='RockfordIL'>Rockford, IL</option>
                                <option key='BelvidereIL' value='BelvidereIL'>Belvidere, IL</option>
                                <option key='DixonIL' value='DixonIL'>Dixon, IL</option>
                                <option key='ElizabethIL' value='ElizabethIL'>Elizabeth, IL</option>
                                <option key='FreeportIL' value='FreeportIL'>Freeport, IL</option>
                                <option key='Class_Other' value='Class_Other'>Other (contact Rock Valley College)</option>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FormControl name='useSign' fullWidth sx={classes.formControl} {...register('useSign')}>
                            <FormLabel shrink={true} sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                                Hearing Impaired
                            </FormLabel>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="useSign"
                                        color='primary'
                                        defaultChecked={false}
                                        sx={{marginLeft: '0', paddingLeft: '0'}}
                                    />
                                }
                                label={<Typography component="span" sx={{fontSize: '1rem'}}>I need a sign language interpreter</Typography>}
                                sx={{...classes.checkbox, marginTop: '.5rem', marginLeft: '0'}}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FormControl name='useWheelchair' fullWidth sx={classes.formControl} {...register('useWheelchair')}>
                            <FormLabel shrink={true} sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                                Mobility Impaired
                            </FormLabel>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="useWheelchair"
                                        color='primary'
                                        defaultChecked={false}
                                        sx={{marginLeft: '0', paddingLeft: '0'}}
                                    />
                                }
                                label={<Typography component="span" sx={{fontSize: '1rem'}}>I require a wheelchair accessible facility</Typography>}
                                sx={{...classes.checkbox, marginTop: '.5rem', marginLeft: '0'}}
                            />
                        </FormControl>
                    </Grid>
                </>
            }
            <Grid item xs={12} sm={6} md={4}>
                <FormControl name='priorSupervision' sx={classes.formControl}>
                    <FormLabel shrink={true} sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                        Prior Supervision *
                        <Typography variant='subtitle2' component='div' color='primary'>
                            Have you had supervision for a traffic ticket that was issued within the last 12 months?
                        </Typography>
                    </FormLabel>
                    <RadioGroup
                        row={true}
                        name='priorSupervision'
                        sx={{padding: '0'}}
                        onChange={(event, value) => setRadios({...radios, priorSupervision: value})}
                        aria-required="true"
                    >
                        <FormControlLabel value='Yes' control={<Radio size='small' sx={{paddingTop: '.35rem', paddingBottom: {xs: '.5rem', sm: '0', md: '.5rem'}}} />} label='Yes' />
                        <FormControlLabel value='No' control={<Radio size='small' sx={{paddingTop: '.35rem'}} />} label='No' />
                    </RadioGroup>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <Stack spacing={1}>
                    <FormControl name='ethnicity' fullWidth sx={classes.formControl} {...register('ethnicity')}>
                        <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                            Ethnic Origin
                        </FormLabel>
                        <Select
                            name='ethnicity'
                            native
                            variant='outlined'
                            fullWidth
                            defaultValue=''
                            onChange={(event) => setEthnicity(event.target.value)}
                            sx={{height: '2rem'}}
                            inputProps={{
                                "aria-required": "true"
                            }}
                        >
                            <option key='none' value=''>Prefer not to respond</option>
                            <option key='A' value='A'>Asian or Pacific Islander</option>
                            <option key='I' value='I'>American Indian/Alaska Native</option>
                            <option key='B' value='B'>Black Non Hispanic</option>
                            <option key='H' value='H'>Hispanic</option>
                            <option key='W' value='W'>White Non Hispanic</option>
                            <option key='N' value='N'>Non-Resident Alien</option>
                            <option key='O' value='O'>Other</option>
                        </Select>
                    </FormControl>
                    { ethnicity && ethnicity === 'O' &&
                        <FormControl name='otherEthnicity' fullWidth sx={classes.formControl} {...register('otherEthnicity')}>
                            <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                                Other Ethnic Origin
                            </FormLabel>
                            <TextField name='otherEthnicity' variant='outlined' fullWidth autoComplete='off' inputProps={{maxLength: 50, sx: classes.textbox, "aria-required": "true"}} />
                        </FormControl>
                    }
                </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <Stack spacing={1}>
                    <FormControl name='education' fullWidth sx={classes.formControl} {...register('education')}>
                        <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                            Highest Degree Earned
                        </FormLabel>
                        <Select
                            name='education'
                            native
                            variant='outlined'
                            fullWidth
                            defaultValue=''
                            onChange={(event) => setEducation(event.target.value)}
                            sx={{height: '2rem'}}
                            inputProps={{
                                "aria-required": "true"
                            }}
                        >
                            <option key='none' value=''>Prefer not to respond</option>
                            <option key='P' value='P'>PhD</option>
                            <option key='F' value='F'>1st Professional Degree</option>
                            <option key='M' value='M'>Master's Degree</option>
                            <option key='A' value='A'>Associate's Degree</option>
                            <option key='C' value='C'>Certificate</option>
                            <option key='S' value='S'>Some College</option>
                            <option key='H' value='H'>High School Diploma</option>
                            <option key='N' value='N'>None</option>
                            <option key='O' value='O'>Other</option>
                        </Select>
                    </FormControl>
                    { education && education === 'O' &&
                        <FormControl name='otherEducation' fullWidth sx={classes.formControl} {...register('otherEducation')}>
                            <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                                Other Degree
                            </FormLabel>
                            <TextField name='otherEducation' variant='outlined' fullWidth autoComplete='off' inputProps={{maxLength: 50, sx: classes.textbox, "aria-required": "true"}} />
                        </FormControl>
                    }
                </Stack>
            </Grid>
        </Grid>
    );
}

export default RockVy;
